import type { SubscriptionPeriod, SubscriptionStatus } from '@/types/platform-subscription.types';
import { cn } from '@/lib/cn';

/**
 * Phase 12.4E — shared Subscriptions presentation.
 *
 * One badge and one set of formatters, used by the list and the detail page —
 * the Companies precedent, for the same reason: two surfaces keeping private
 * copies of a status palette is how they end up disagreeing about what
 * "Suspended" looks like.
 */

const STATUS_BADGE: Record<SubscriptionStatus, string> = {
  ACTIVE: 'bg-green-500/10 text-green-700 dark:text-green-400',
  TRIAL: 'bg-amber-500/10 text-amber-700 dark:text-amber-400',
  SUSPENDED: 'bg-destructive/10 text-destructive',
  CANCELLED: 'bg-muted text-muted-foreground',
  EXPIRED: 'bg-muted text-muted-foreground',
};

/**
 * Status is conveyed by TEXT as well as colour, so a reader who cannot
 * distinguish amber from green still reads "Trial" and "Active".
 *
 * `EXPIRED` is listed because it exists in the vocabulary and could in principle
 * be read from a row — **not** because anything produces it. Nothing does: there
 * is no expiry transition (D-10), and the console offers no path to one.
 */
const STATUS_LABEL: Record<SubscriptionStatus, string> = {
  ACTIVE: 'Active',
  TRIAL: 'Trial',
  SUSPENDED: 'Suspended',
  CANCELLED: 'Cancelled',
  EXPIRED: 'Expired',
};

const PERIOD_LABEL: Record<SubscriptionPeriod, string> = {
  TRIAL: 'Trial',
  MONTHLY: 'Monthly',
  QUARTERLY: 'Quarterly',
  ANNUAL: 'Annual',
};

export function SubscriptionStatusBadge({
  status,
  size = 'sm',
}: {
  status: SubscriptionStatus;
  size?: 'sm' | 'md';
}) {
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center rounded-full font-medium',
        size === 'md' ? 'px-3 py-1 text-xs font-semibold' : 'px-2.5 py-0.5 text-xs',
        STATUS_BADGE[status],
      )}
    >
      {STATUS_LABEL[status]}
    </span>
  );
}

export function subscriptionStatusLabel(status: SubscriptionStatus): string {
  return STATUS_LABEL[status];
}

/**
 * The period, or an em dash.
 *
 * **Nothing is computed here.** GP-4 is resolved as Option A (D-109 §2): every
 * existing row has `period = null`, and `null` means *"no term recorded"* — it
 * does not mean trial, monthly, or anything that could be worked out from
 * `startedAt`. An em dash is the honest rendering of an absent fact.
 */
export function formatPeriod(period: SubscriptionPeriod | null | undefined): string {
  if (!period) return '—';
  return PERIOD_LABEL[period] ?? '—';
}

/** Date only — the list's Started and Expires columns. */
export function formatSubscriptionDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

/** Date and time — detail, where "when exactly" matters. */
export function formatSubscriptionDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * The expiry cell.
 *
 * Deliberately identical to `formatSubscriptionDate` rather than "smarter". No
 * countdown, no "30 days remaining", no "expires soon", no "never expires" — the
 * console must not compute or characterise an expiry it was never given
 * (D-109 §2, D-15). If a date is stored it is shown; otherwise, an em dash.
 */
export function formatExpiry(iso: string | null | undefined): string {
  return formatSubscriptionDate(iso);
}
